// Floating bottom-right download tray. Mounted once at App root via
// DownloadTray itself (no router awareness); reads from the download manager
// context. Renders nothing when there are no tasks.

import { useState } from "react";
import { useDownloadManager, type DownloadTask } from "@/features/exports/services/DownloadManagerContext";
import { Button } from "@/shared/ui/button";
import { Progress } from "@/shared/ui/progress";
import { Badge } from "@/shared/ui/badge";
import { ChevronDown, ChevronUp, Download, X, RotateCcw } from "lucide-react";

export function DownloadTray() {
  const { tasks, cancel, dismiss, saveAgain, clearCompleted } = useDownloadManager();
  const [collapsed, setCollapsed] = useState(false);

  if (tasks.length === 0) return null;

  const active = tasks.filter((t) => t.status === "running" || t.status === "queued").length;
  const totalDone = tasks.filter((t) => t.status === "complete").length;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 border-b border-border"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          <Download className="h-4 w-4" />
          {active > 0
            ? `Downloads · ${active} in progress`
            : `Downloads · ${totalDone} ready`}
        </span>
        {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {!collapsed && (
        <>
          <div className="max-h-72 overflow-y-auto p-2 space-y-2">
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                onCancel={() => cancel(task.id)}
                onDismiss={() => dismiss(task.id)}
                onSaveAgain={() => saveAgain(task.id)}
              />
            ))}
          </div>
          {tasks.some((t) => t.status === "complete" || t.status === "error" || t.status === "cancelled") && (
            <div className="px-2 pb-2">
              <Button variant="ghost" size="sm" className="w-full text-xs" onClick={clearCompleted}>
                Clear finished
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TaskRow({
  task,
  onCancel,
  onDismiss,
  onSaveAgain,
}: {
  task: DownloadTask;
  onCancel: () => void;
  onDismiss: () => void;
  onSaveAgain: () => void;
}) {
  return (
    <div className="rounded border border-border p-2 text-xs space-y-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate" title={task.label}>{task.label}</div>
          {task.detail && (
            <div className="text-muted-foreground text-[11px] truncate">{task.detail}</div>
          )}
          {task.error && (
            <div className="text-destructive text-[11px] truncate" title={task.error}>{task.error}</div>
          )}
        </div>
        <StatusBadge status={task.status} />
      </div>

      {(task.status === "running" || task.status === "queued") && (
        <Progress value={task.progress} className="h-1.5" />
      )}

      <div className="flex items-center justify-end gap-1">
        {(task.status === "running" || task.status === "queued") && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={onCancel}>
            <X className="h-3 w-3 mr-1" /> Cancel
          </Button>
        )}
        {task.status === "complete" && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={onSaveAgain}>
            <Download className="h-3 w-3 mr-1" /> Save again
          </Button>
        )}
        {(task.status === "complete" || task.status === "error" || task.status === "cancelled") && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={onDismiss}>
            <X className="h-3 w-3" />
          </Button>
        )}
        {task.status === "error" && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" disabled title="Retry not wired up yet">
            <RotateCcw className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: DownloadTask["status"] }) {
  const map: Record<DownloadTask["status"], { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    queued: { label: "queued", variant: "secondary" },
    running: { label: "running", variant: "default" },
    complete: { label: "ready", variant: "outline" },
    error: { label: "error", variant: "destructive" },
    cancelled: { label: "cancelled", variant: "secondary" },
  };
  const { label, variant } = map[status];
  return <Badge variant={variant} className="text-[10px] h-5">{label}</Badge>;
}
